import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const UserEditDialog = ({ user, onSaved, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', loyalty_points: 0, is_premium: false });
  useEffect(() => {
    if (user) setForm({ name: user.name || '', email: user.email || '', loyalty_points: user.loyalty_points || 0, is_premium: !!user.is_premium });
  }, [user]);
  const save = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, { ...form, loyalty_points: Number(form.loyalty_points) });
      toast.success('User updated');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="user-edit-dialog">
        <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name-input" /></div>
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-edit-email-input" /></div>
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Loyalty points</label>
            <Input type="number" value={form.loyalty_points} onChange={(e) => setForm({ ...form, loyalty_points: e.target.value })} data-testid="user-edit-loyalty-input" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_premium} onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} data-testid="user-edit-premium-checkbox" />
            <span>Jwalek Premium member</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="user-edit-save-button" className="jw-btn-glossy">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UsersSection = () => {
  const { refreshStats } = useOutletContext();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState('all');
  const [archived, setArchived] = useState('false');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const sel = useSelection(users);
  const [pointsInput, setPointsInput] = useState('');

  const load = (p = page) => {
    const params = { role, archived, search, page: p, page_size: 25 };
    api.get('/admin/users', { params }).then(({ data }) => {
      setUsers(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setPage(data.page || p);
      sel.clear();
    });
  };
  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [role, archived]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setUserRole = async (id, newRole) => {
    const ok = await confirm({
      title: `Change role to "${newRole}"?`,
      description: newRole === 'admin' ? 'This user will gain full admin access.' : 'This user will lose admin privileges.',
      confirmLabel: 'Change role',
    });
    if (!ok) return;
    try { await api.patch(`/admin/users/${id}/role?role=${newRole}&confirm=true`); toast.success('Role updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const archiveUser = async (id, val) => {
    const ok = await confirm({
      title: val ? 'Archive this user?' : 'Restore this user?',
      description: val ? 'They will be hidden from the active list.' : 'They will return to active users.',
      confirmLabel: val ? 'Archive' : 'Restore',
    });
    if (!ok) return;
    try { await api.patch(`/admin/users/${id}/archive?archived=${val}`); toast.success(val ? 'Archived' : 'Restored'); load(); refreshStats(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteUser = async (id, email) => {
    const ok = await confirm({
      title: 'Delete this user permanently?',
      description: `${email}: all their cart items, favorites, saved designs, and notifications will be removed. This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      variant: 'danger',
    });
    if (!ok) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('User deleted'); load(); refreshStats(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const bulkRun = async (action, extra = {}) => {
    try {
      const { data } = await api.post('/admin/users/bulk', { ids: sel.selected, action, ...extra });
      toast.success(`Applied to ${data.affected} user${data.affected === 1 ? '' : 's'}`);
      load(); refreshStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    }
  };

  const bulkArchive = async (val) => {
    const ok = await confirm({
      title: val ? `Archive ${sel.size} user${sel.size === 1 ? '' : 's'}?` : `Restore ${sel.size} user${sel.size === 1 ? '' : 's'}?`,
      description: val ? 'They will be hidden from the active list.' : 'They will return to active users.',
      confirmLabel: val ? 'Archive selected' : 'Restore selected',
    });
    if (!ok) return;
    bulkRun(val ? 'archive' : 'unarchive');
  };

  const bulkDelete = async () => {
    const ok = await confirm({
      title: `Delete ${sel.size} user${sel.size === 1 ? '' : 's'} permanently?`,
      description: 'All their cart items, favorites, saved designs and notifications are removed. Cannot be undone.',
      confirmLabel: 'Delete permanently',
      variant: 'danger',
    });
    if (!ok) return;
    bulkRun('delete');
  };

  const bulkAddPoints = async () => {
    const n = parseInt(pointsInput, 10);
    if (!Number.isFinite(n) || n === 0) { toast.error('Enter a non-zero integer'); return; }
    bulkRun('add_points', { value: n });
    setPointsInput('');
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-40">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
          <Input placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-users-search-input" className="pl-9" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-36" data-testid="admin-users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="customer">Customer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={archived} onValueChange={setArchived}>
          <SelectTrigger className="w-36" data-testid="admin-users-archived-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="false">Active</SelectItem>
            <SelectItem value="true">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setBroadcastOpen(true)} data-testid="admin-users-broadcast-button" className="jw-btn-gloss-ghost h-9"><Bell className="w-4 h-4 mr-1" /> Broadcast</Button>
      </div>
      <Card className="jw-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={sel.allSelected}
                    ref={(el) => { if (el) el.indeterminate = sel.someSelected; }}
                    onChange={sel.toggleAll}
                    data-testid="admin-users-select-all"
                    aria-label="Select all users on this page"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Loyalty</TableHead>
                <TableHead>Premium</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}-row`} data-selected={sel.has(u.id) ? 'true' : 'false'}>
                  <TableCell>
                    <input type="checkbox" checked={sel.has(u.id)} onChange={() => sel.toggle(u.id)} data-testid={`admin-user-${u.id}-select`} aria-label={`Select user ${u.email}`} />
                  </TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell style={{ color: 'var(--jw-text-muted)' }}>{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => setUserRole(u.id, v)}>
                      <SelectTrigger className="w-32" data-testid={`admin-user-${u.id}-role-select`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="customer">Customer</SelectItem></SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{u.loyalty_points || 0}</TableCell>
                  <TableCell>{u.is_premium ? <Badge className="jw-gradient-bg text-white text-[10px]">PREMIUM</Badge> : <span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>—</span>}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {u.publish_muted_at && (
                      <Button size="sm" variant="ghost" onClick={async () => {
                        try { await api.post(`/admin/users/${u.id}/unmute-publishing`); toast.success('Publishing restored'); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }} title="Restore publishing" data-testid={`admin-user-${u.id}-unmute-button`} className="text-amber-300 h-8">
                        <ShieldAlert className="w-4 h-4 mr-1" /> Muted
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setEditing(u)} title="Edit" data-testid={`admin-user-${u.id}-edit-button`}><Edit3 className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => archiveUser(u.id, !u.archived)} title={u.archived ? 'Restore' : 'Archive'} data-testid={`admin-user-${u.id}-archive-button`}>
                      {u.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteUser(u.id, u.email)} title="Delete" data-testid={`admin-user-${u.id}-delete-button`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8" style={{ color: 'var(--jw-text-muted)' }}>No users found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 pb-4">
          <Pagination page={page} pages={pages} total={total} onChange={(p) => { setPage(p); load(p); }} testIdPrefix="admin-users-pagination" />
        </div>
      </Card>

      <BulkActionBar
        count={sel.size}
        onClear={sel.clear}
        testIdPrefix="admin-users-bulk"
        actions={[
          { key: 'grant-premium', label: 'Grant Premium', icon: Crown, onClick: () => bulkRun('grant_premium') },
          { key: 'revoke-premium', label: 'Revoke Premium', onClick: () => bulkRun('revoke_premium') },
          { key: 'archive', label: 'Archive', icon: Archive, onClick: () => bulkArchive(true) },
          { key: 'unarchive', label: 'Restore', icon: ArchiveRestore, onClick: () => bulkArchive(false) },
          { key: 'delete', label: 'Delete', icon: Trash2, onClick: bulkDelete, variant: 'danger' },
        ]}
      />
      {sel.size > 0 && (
        <div className="jw-card sticky bottom-24 z-30 mt-2 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="admin-users-points-bar" style={{ background: 'var(--jw-bg-alt)', borderColor: 'var(--jw-border)' }}>
          <Award className="w-4 h-4 text-orange-300" />
          <span className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Grant/subtract loyalty points to selected:</span>
          <Input type="number" value={pointsInput} onChange={(e) => setPointsInput(e.target.value)} placeholder="e.g. 100 or -50" data-testid="admin-users-bulk-points-input" className="w-40 h-9" />
          <Button size="sm" onClick={bulkAddPoints} data-testid="admin-users-bulk-points-button" className="jw-btn-gloss-ghost h-9">Apply</Button>
        </div>
      )}
      <UserEditDialog user={editing} onSaved={load} onClose={() => setEditing(null)} />
      <BroadcastDialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
    </div>
  );
};

const BroadcastDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ title: '', message: '', target: 'all' });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.title || !form.message) { toast.error('Enter title and message'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/notifications/broadcast', form);
      toast.success(`Sent to ${data.sent} users`);
      setForm({ title: '', message: '', target: 'all' });
      onClose();
    } catch { toast.error('Send failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="broadcast-dialog">
        <DialogHeader><DialogTitle>Broadcast notification</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Title</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="broadcast-title-input" /></div>
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Message</label>
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="broadcast-message-input" /></div>
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Target</label>
            <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
              <SelectTrigger data-testid="broadcast-target-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All users</SelectItem><SelectItem value="customers">Customers only</SelectItem><SelectItem value="admins">Admins only</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="broadcast-send-button" className="jw-btn-glossy"><Send className="w-4 h-4 mr-1" /> {busy ? 'Sending...' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default UsersSection;
